/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : AddIntegrations.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton, InputAdornment
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { HandleApiActions } from "../../redux/actions";
import integrationConfigAction from "../../redux/actions/integrationConfigAction";
import CustomSelectV2 from "../../components/customComponentsV2/common/select/CustomSelectV2";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import JiraIcon from "../../assets/Jira.png";
import GitHub from "../../assets/gitPng.png";
import XrayIcon from "../../assets/PikPng.com_xray-png_5613333.png";
import ServiceNowIcon from "../../assets/ServiceNow_idec6JyuYi_1.png";
import PTC from "../../assets/PTC.png";
import { toast } from "react-toastify";

function AddIntegrations({
  open,
  onClose,
  selectedModule,
  fetchIntegrationDetails,
}) {
  const dispatch = useDispatch();

  const [selectedType, setSelectedType] = useState("");
  const [selectedPage, setSelectedPage] = useState("");
  const [formValues, setFormValues] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [getAllConnectors, setGetAllConnectors] = useState([]);
  const [showPassword, setShowPassword] = useState(false);

  // Available pages/modules
  const availablePages = [
    { value: "PROJECT CONFIGURATOR", label: "Project Configurator" },
    { value: "VIRTUAL LAB-CAR", label: "Virtual Lab-Car" },
    { value: "LEGAL REQUIREMENTS", label: "Legal Requirements" },
    { value: "TEST CASES", label: "Test Cases" },
    { value: "INTERSECTION", label: "Regulatory Compliance" },
  ];

  useEffect(() => {
    const fetchConnectorDetails = async () => {
      const resp = await dispatch(
        HandleApiActions({
          ...integrationConfigAction.get_connectors,
        }),
      );
      if (!resp?.error) {
        setGetAllConnectors(resp.data);
      }
    };
    fetchConnectorDetails();
  }, []);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedType("");
      setSelectedPage("");
      setFormValues({});
      setFormErrors({});
      setShowPassword(false);
    }
  }, [open]);

  const selectedIntegration = getAllConnectors?.find(
    (i) => i.type === selectedType,
  );

  const getToolIcon = (toolName) => {
    const iconMap = {
      JIRA: JiraIcon,
      "JIRA Software": JiraIcon,
      "JIRA Xray": XrayIcon,
      "JIRA Xray Test Case Management": XrayIcon,
      ServiceNow: ServiceNowIcon,
      GitHub: GitHub,
      CodeBeamer: PTC,
    };

    // Check exact match first
    let result = iconMap[toolName];

    // Fallback logic for tool name variations
    if (!result && toolName) {
      const lowerToolName = toolName.toLowerCase();
      if (lowerToolName.includes("jira") || lowerToolName.includes("xray")) {
        result = lowerToolName.includes("xray") ? XrayIcon : JiraIcon;
      } else if (
        lowerToolName.includes("service") &&
        lowerToolName.includes("now")
      ) {
        result = ServiceNowIcon;
      } else if (
        lowerToolName.includes("servicenow") ||
        lowerToolName.includes("snow")
      ) {
        result = ServiceNowIcon;
      } else if (lowerToolName.includes("codebeamer")) {
        result = PTC;
      } else if (
        lowerToolName.includes("github") ||
        lowerToolName.includes("git")
      ) {
        result = GitHub;
      }
    }

    return result || null;
  };

  const handleAddIntegration = async (formValues) => {

    // Step 1: Test the connection
    const testReqObj = {
      // id: parseInt(selectedItem.id),
      type: selectedIntegration?.type?.toLowerCase(),
      auth: formValues?.auth,
      options: formValues?.options,
      token: localStorage.getItem("token") || sessionStorage.getItem("token"),
    };

    const testResp = await dispatch(
      HandleApiActions({
        ...integrationConfigAction.test_connectors,
        params: testReqObj,
        show_toast: false, // we'll manage manually
      }),
    );
    const object = {
      connector_type: selectedIntegration?.type?.toUpperCase(),
      module_type: selectedPage, // Use selected page from dropdown
      auth: formValues?.auth,
      options: formValues?.options,
    };
    if (testResp?.ok === true) {
      const reqObj = {
        connector_type: selectedIntegration?.type?.toUpperCase(),
        module_type: selectedPage, // Use selected page from dropdown
        auth: formValues?.auth,
        options: formValues?.options,
      };

      const resp = await dispatch(
        HandleApiActions({
          ...integrationConfigAction.add_integrations,
          params: reqObj,
          show_toast: true,
        }),
      );
      if (!resp?.error) {
        fetchIntegrationDetails();
        onClose();
      }
    } else {
      // Case: Connection test failed completely (ok == false)
      toast.error(
        // testResp?.message ||
        "Connection test failed. Please check your configuration.",
      );
    }
  };

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const isFormValid = () => {
    if (!selectedType || !selectedPage) return false;

    if (!selectedIntegration) return false;

    // Check auth fields
    const authValid = (selectedIntegration.auth?.fields || []).every(
      (field) => {
        if (field.required) {
          return formValues.auth?.[field.key]?.trim();
        }
        return true;
      },
    );

    // Check option fields
    const optionsValid = (selectedIntegration.options || []).every((option) => {
      if (option.required) {
        return formValues.options?.[option.key]?.trim();
      }
      return true;
    });

    return authValid && optionsValid;
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            minHeight: "500px",
            height: "500px",
            borderRadius: 4,
            // width: "100%",
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "1.1rem" }}>Add Integrations</DialogTitle>
        <DialogContent
          sx={{
            overflow: "auto",
            // Custom scrollbar styling
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#a8a8a8",
            },
          }}
        >
          {" "}
          {/* Set minimum content height and enable scrolling */}
          {/* Integration Type Dropdown */}
          <CustomSelectV2
            label="Select Tool"
            required={true}
            optionsList={getAllConnectors?.map((integration) => ({
              label: integration.name,
              value: integration.type,
              icon: getToolIcon(integration.name),
            }))}
            selectedValue={selectedType || " "}
            handleOnChange={(e) => setSelectedType(e.target.value)}
            sltFldStyle={{ mb: 0 }}
          />
          {/* Page Selection Dropdown */}
          <CustomSelectV2
            label="Select Page"
            required={true}
            optionsList={availablePages}
            selectedValue={selectedPage || " "}
            handleOnChange={(e) => setSelectedPage(e.target.value)}
            sltFldStyle={{ mb: 0 }}
          />
          {/* Content Area - Always visible with consistent height */}
          <Box
            sx={{
              // border: "1px solid #e0e0e0",
              // borderRadius: 1,
              // p: 2,
              // backgroundColor: "#fafafa",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {!selectedIntegration ? (
              // Instructions when no integration type is selected
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  textAlign: "left",
                  mt: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Please select an tool type above to configure the connection
                  settings.
                  <br />
                  <br />
                  You will need to provide authentication credentials and
                  configuration options specific to the selected tool.
                  <br />
                  <br />
                  Each tool type has different requirements for setup and
                  configuration.
                </Typography>
              </Box>
            ) : (
              // Dynamic Fields when integration type is selected
              <Box sx={{ width: "100%" }}>
                {/* <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mb: 2 }}
                >
                  Authentication
                </Typography> */}
                {selectedIntegration?.auth?.fields.map((field) => (
                  <CustomFormTextFieldV2
                    key={field.key}
                    size="small"
                    label={field.label}
                    type={
                      field.type === "password" && !showPassword
                        ? "password"
                        : "text"
                    }
                    fldType={field.key === "email" ? "email" : "text"}
                    placeholder={field.label}
                    required={field.required}
                    value={formValues.auth?.[field.key] || ""}
                    setValue={(value) => {
                      setFormValues((prev) => ({
                        ...prev,
                        auth: {
                          ...prev.auth,
                          [field.key]: value,
                        },
                      }));
                    }}
                    valueError={formErrors.auth?.[field.key] || ""}
                    setValueError={(error) => {
                      setFormErrors((prev) => ({
                        ...prev,
                        auth: {
                          ...prev.auth,
                          [field.key]: error,
                        },
                      }));
                    }}
                    variant="outlined"
                    txtFldStyle={{ mb: 2 }}
                    inputProps={
                      field.type === "password"
                        ? {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleTogglePassword}
                                edge="end"
                              >
                                {showPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }
                        : {}
                    }
                  />
                ))}

                {selectedIntegration.options &&
                  selectedIntegration.options.length > 0 && (
                    <>

                      {selectedIntegration.options?.map((option) => (
                        <CustomFormTextFieldV2
                          key={option.key}
                          size="small"
                          label={option.label}
                          type="text"
                          placeholder={option.label}
                          required={option.required}
                          value={formValues.options?.[option.key] || ""}
                          setValue={(value) => {
                            setFormValues((prev) => ({
                              ...prev,
                              options: {
                                ...prev.options,
                                [option.key]: value,
                              },
                            }));
                          }}
                          valueError={formErrors.options?.[option.key] || ""}
                          setValueError={(error) => {
                            setFormErrors((prev) => ({
                              ...prev,
                              options: {
                                ...prev.options,
                                [option.key]: error,
                              },
                            }));
                          }}
                          variant="outlined"
                          txtFldStyle={{ mb: 2 }}
                        />
                      ))}
                    </>
                  )}
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ pr: 2, pb: 2 }}>
          <CustomButtonV2
            handleOnClick={onClose}
            type={"cancel"}
            btnName={"Cancel"}
            label="Cancel"
          />
          <CustomButtonV2
            handleOnClick={() => handleAddIntegration(formValues)}
            disabled={!isFormValid()}
            label="Test connect & Add"
            btnName={"Test connect & Add"}
          />
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default AddIntegrations;

